import StatusIcon, { StatusIconModifier } from 'components/statusIcon';
import Tooltip, {TooltipAlignEnum, TooltipCursorEnum, TooltipModifier} from 'components/tooltip';
import {TFunction, useTranslation} from 'react-i18next';
import {StatusEnum} from 'models/general';
import IconList, { IconListModifier } from 'components/iconList';
import { ValidationTableNotices, ValidationTableWarnings } from 'models/validation';

export enum ValidationStatusTooltipKind {
    WARNINGS,
    NOTICES,
    BOTH
}

export interface ValidationStatusTooltipProps {
  warnings: ValidationTableWarnings;
  notices: ValidationTableNotices;
  show: ValidationStatusTooltipKind;
}

export const hasItems = (items: ValidationTableWarnings | ValidationTableNotices): boolean => {
    for (const [key, value] of Object.entries(items)) {
        if (value) {
            return true;
        }
    }
    return false;
};

const getStatus = (items: ValidationStatusTooltipProps): StatusEnum => {
    switch (items.show) {
        case ValidationStatusTooltipKind.WARNINGS:
            if (hasItems(items.warnings)) {
                return StatusEnum.ERROR;
            }
            break
        case ValidationStatusTooltipKind.NOTICES:
            if (hasItems(items.notices)) {
                return StatusEnum.WARNING;
            }
            break
        case ValidationStatusTooltipKind.BOTH:
            if (hasItems(items.warnings)) {
                return StatusEnum.ERROR;
            }
            if (hasItems(items.notices)) {
                return StatusEnum.WARNING;
            }
            break
    }
    return StatusEnum.SUCCESS;
};


function getWarningsText(props: ValidationStatusTooltipProps, t: TFunction<"translation", undefined>): string[] {
  let textValue: string[] = [];
  const { warnings } = props;

    if (warnings.is_transaction_type_incorrect) {
        textValue.push(
            t('transaction type incorrect', { ns: 'validation' })
        );
    }
    if (warnings.is_buy_amount_negative_or_exceeding_the_limit) {
        textValue.push(
            t('buy amount negative or exceeding the limit', { ns: 'validation' })
        );
    }
    if (warnings.is_buy_coin_not_set) {
        textValue.push(
            t('buy coin not set', { ns: 'validation' })
        );
    }
    if (warnings.is_buy_coin_contains_not_allowed_characters) {
        textValue.push(
            t('buy coin contains not allowed characters', { ns: 'validation' })
        );
    }
    if (warnings.is_incoming_transaction_also_contains_outgoing_values) {
        textValue.push(
            t('incoming transaction also contains outgoing values', { ns: 'validation' })
        );
    }
    if (warnings.is_sell_amount_negative_or_exceeding_the_limit) {
        textValue.push(
            t('sell amount negative or exceeding the limit', { ns: 'validation' })
        );
    }
    if (warnings.is_sell_coin_not_set) {
        textValue.push(
            t('sell coin not set', { ns: 'validation' })
        );
    }
    if (warnings.is_sell_coin_contains_not_allowed_characters) {
        textValue.push(
            t('sell coin contains not allowed characters', { ns: 'validation' })
        );
    }
    if (warnings.is_outgoing_transaction_also_contains_incoming_values) {
        textValue.push(
            t('outgoing transaction also contains incoming values', { ns: 'validation' })
        );
    }
    if (warnings.is_fee_amount_negative_or_exceeding_the_limit) {
        textValue.push(
            t('fee amount negative or exceeding the limit', { ns: 'validation' })
        );
    }
    if (warnings.is_fee_coin_not_set) {
        textValue.push(
            t('fee coin not set', { ns: 'validation' })
        );
    }
    if (warnings.is_fee_coin_contains_not_allowed_characters) {
        textValue.push(
            t('fee coin contains not allowed characters', { ns: 'validation' })
        );
    }
    if (warnings.is_transaction_date_before_the_limit) {
        textValue.push(
            t('transaction date before the limit', { ns: 'validation' })
        );
    }
    if (warnings.is_transaction_date_more_than_thirty_days_in_the_future) {
        textValue.push(
            t('transaction date more than thirty days in the future', { ns: 'validation' })
        );
    }
    if (warnings.is_there_white_space_at_the_beginning_of_exchange_name) {
        textValue.push(
            t('white space at the beginning of exchange name', { ns: 'validation' })
        );
    }
    if (warnings.is_there_white_space_at_the_end_of_exchange_name) {
        textValue.push(
            t('white space at the end of exchange name', { ns: 'validation' })
        );
    }
    if (warnings.is_there_white_space_at_the_beginning_of_trade_group) {
        textValue.push(
            t('white space at the beginning of trade group', { ns: 'validation' })
        );
    }
    if (warnings.is_there_white_space_at_the_end_of_trade_group) {
        textValue.push(
            t('white space at the end of trade group', { ns: 'validation' })
        );
    }
    if (warnings.is_there_white_space_at_the_beginning_of_comment) {
        textValue.push(
            t('white space at the beginning of comment', { ns: 'validation' })
        );
    }
    if (warnings.is_there_white_space_at_the_end_of_comment) {
        textValue.push(
            t('white space at the end of comment', { ns: 'validation' })
        );
    }
    return textValue;
}

function getNoticesText(props: ValidationStatusTooltipProps, t: TFunction<"translation", undefined>): string[] {
  let textValue: string[] = [];
  const { notices } = props;

    if (notices.is_asset_values_not_matching) {
        textValue.push(
            t('asset values not matching', { ns: 'validation' })
        );
    }
    if (notices.is_asset_value_missing) {
        textValue.push(
            t('asset value missing', { ns: 'validation' })
        );
    }
  return textValue;
}

function ValidationStatusTooltip(props: ValidationStatusTooltipProps) {
  const { t } = useTranslation();

  let textValue: string[] = []

  switch (props.show) {
    case ValidationStatusTooltipKind.WARNINGS:
      if (hasItems(props.warnings)) {
        textValue = getWarningsText(props, t);
      }
      break
    case ValidationStatusTooltipKind.NOTICES:
      if (hasItems(props.notices)) {
        textValue = getNoticesText(props, t);
      }
      break
    case ValidationStatusTooltipKind.BOTH:
      if (hasItems(props.warnings)) {
        textValue = getWarningsText(props, t);
      }
      if (hasItems(props.notices)) {
        textValue = getNoticesText(props, t);
      }
      break
    }

  const title: string[] = textValue.filter((v) => v !== null);
  const statusValue = getStatus(props);

  return (
    <>
    {title.length > 0 ? (
            <Tooltip
              align={TooltipAlignEnum.RIGHT}
              cursor={TooltipCursorEnum.HELP}
              modifiers={[TooltipModifier.VALIDATION]}
              tooltipChildren={(
                <IconList
                  items={title}
                  modifiers={[
                    IconListModifier.TABLE_SPACING, IconListModifier.OLD,
                  ]}
                />
              )}
            >
              <StatusIcon type={statusValue} modifiers={[StatusIconModifier.VALIDATION]} />
            </Tooltip>
          ) : <StatusIcon type={statusValue} modifiers={[StatusIconModifier.VALIDATION]} />
          }
    </>
  );
}

ValidationStatusTooltip.defaultProps = { ...{ } };

export default ValidationStatusTooltip;